import { EFinancingProgram, EProvince } from '@if/core/types'
import { Constants } from '@src/data/types'
import { NormCreditDecision } from '@src/data/types/CreditDecisionSchema'
import { FinancingConfigDto } from '@src/data/types/FinancingConfigDto'
import { Merchant } from '@src/data/types/Merchant'
import { BaseWorksheetFormConfig } from './BaseWorksheetForm.config'

type ProgramConfig = (typeof Constants.financingProgramConfigs)[keyof typeof Constants.financingProgramConfigs]

export const resolveProgramConfig = (financingProgramId: EFinancingProgram): ProgramConfig =>
  Constants.financingProgramConfigs[financingProgramId] ?? Constants.financingProgramConfigs.default

const mergeLoanAmount = (financingValue: number, programFallback: number | undefined): number => {
  if (financingValue > 0) return financingValue
  if (programFallback && programFallback > 0) return programFallback
  return 0
}

export interface BuildBaseWorksheetFormConfigInput {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  merchantId: string
  financingConfig: FinancingConfigDto
  stateIso: EProvince
  hasCoApplicant: boolean
  createdOn: Date
  expiresOn: Date | string
  merchant?: Merchant | null
  normsCreditDecision?: NormCreditDecision | null
  maxAmountFinanced?: number | null
  maxTermDuration?: number | null
  editLocked?: boolean
  isSinglePayment?: boolean
}

export const buildBaseWorksheetFormConfig = (input: BuildBaseWorksheetFormConfigInput): BaseWorksheetFormConfig => {
  const {
    financingProgramId,
    creditApplicationId,
    merchantId,
    financingConfig,
    stateIso,
    hasCoApplicant,
    createdOn,
    expiresOn,
    merchant,
    normsCreditDecision,
    maxAmountFinanced,
    maxTermDuration,
    editLocked,
    isSinglePayment,
  } = input

  const programConfig = resolveProgramConfig(financingProgramId)

  return {
    financingProgramId,
    creditApplicationId,
    merchantId,
    minLoanAmount: mergeLoanAmount(financingConfig.minimumLoanAmount, programConfig.minLoanAmount),
    maxLoanAmount: mergeLoanAmount(financingConfig.maximumLoanAmount, programConfig.maxLoanAmount),
    minLoanTerm: financingConfig.minLoanTerm,
    maxLoanTerm: financingConfig.maxLoanTerm,
    largeLoanAmount: financingConfig.largeLoanAmount,
    effectiveRateThreshold: financingConfig.effectiveRateThreshold,
    supportedPaymentFrequencies: programConfig.supportedPaymentFrequencies,
    supportsInsurance: programConfig.supportsInsurance,
    supportsHighEstimate: programConfig.supportsHighEstimate,
    useAgentLeeway: programConfig.useAgentLeeway,
    validateMaxPayment: programConfig.validateMaxPaymentAmountInWorksheet,
    filteredTermsByMaxPayment: programConfig.filteredTermsByMaxPayment,
    checkAvailableCredit: programConfig.checkAvailableCredit,
    checkActivationWithin90Days: programConfig.checkActivationWithin90Days,
    normsCreditDecision: normsCreditDecision ?? null,
    maxAmountFinanced,
    maxTermDuration,
    canAcceptHighEstimate: merchant?.canAcceptHighEstimate,
    hasCoApplicant,
    stateIso,
    createdOn,
    expiresOn,
    editLocked,
    isSinglePayment,
  }
}
